import type React from 'react';
import { type FC, useCallback, useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { createStore, createStateHook, createActionsHook } from 'react-sweet-state';
import type { StoreActionApi } from 'react-sweet-state';

import { type IconProps } from '@atlaskit/icon/types';

import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { expValEquals } from '@confluence/feature-experiments';

import type { PanelName } from './PanelName.ts';

/**
 * IF YOU MAKE CHANGES to MIN_PANEL_WIDTH or the defaultPanelWidth calculation, please ensure you update the width
 * of the 'ObjectSidebarPushPlaceholder' accordingly. Otherwise, the content may not be pushed over the correct amount
 * when the ObjectSidebarPanel is open.
 */
export const CONTENT_RIGHT_PADDING = 40;
// We will always reset the panel to the default size when we close it
export const MIN_PANEL_WIDTH = 400;
export const defaultPanelWidth = Math.max(MIN_PANEL_WIDTH, 368) + CONTENT_RIGHT_PADDING;

// Below type is copied to;
// - platform/packages/editor/editor-plugin-context-panel/src/types/object-siderbar-types.ts
// - platform/packages/insights-ai-platform/ai-smart-button/src/ui/ai-smart-button/object-sidebar-types.ts
export type ObjectSidebarControlState = 'shown' | 'hidden';
// Below type is copied to;
// - platform/packages/editor/editor-plugin-context-panel/src/types/object-siderbar-types.ts
// - platform/packages/insights-ai-platform/ai-smart-button/src/ui/ai-smart-button/object-sidebar-types.ts
export type ObjectSidebarBehavior = 'push' | 'cover';
// Below type is copied to;
// - platform/packages/editor/editor-plugin-context-panel/src/types/object-siderbar-types.ts
// - platform/packages/insights-ai-platform/ai-smart-button/src/ui/ai-smart-button/object-sidebar-types.ts
export type ObjectSidebarPanelCloseOptions = {
	canClosePanel?: () => Promise<boolean>; // Returns true if panel can be closed
	onPanelClose?: () => void;
};

export type ObjectSidebarControlExpandCollapsedState = 'expanded' | 'collapsed';

export type PanelProps = {
	highlightLabels?: boolean;
};

type PanelElement = FC<PanelProps> | (() => React.ReactElement<PanelProps, any> | null);

//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
type ViewConfig = {
	render: () => React.ReactElement;
	headerRightAlignedElement?: React.ReactElement;
};
//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
type AIActionStatus = 'beta' | 'new';

//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
export type AIActionType =
	| 'ai-action'
	| 'agent-action'
	| 'studio-access'
	| 'convo-starter-action'
	| 'help-action';

//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
type AISource = {
	sourceId: string;
};

//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
export type BaseAIAction = {
	id: string;
	title: string;
	description?: string | React.ReactElement;
	icon: React.ReactElement;
	onClick?: () => void;
	viewConfig?: ViewConfig;
	children?: Array<BaseAIAction>;
	closePanelOnSelection?: boolean;
	status?: AIActionStatus;
	type?: AIActionType;
};
//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
export type AIAction = BaseAIAction &
	AISource & {
		children?: Array<AIAction>;
	};
//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
export type BaseNudge = BaseAIAction & {
	nudgeIcon: (props: IconProps) => React.JSX.Element | null;
};
//Copied from @atlassian/ai-smart-button-api/src/services/ai-smart-button-store/index.txs
export type Nudge = BaseNudge &
	AISource & {
		children?: Array<AIAction>;
	};
// Below type is copied to;
// - platform/packages/editor/editor-plugin-context-panel/src/types/object-siderbar-types.ts
// - platform/packages/insights-ai-platform/ai-smart-button/src/ui/ai-smart-button/object-sidebar-types.ts
export type ObjectSidebarPanel = {
	id: PanelName | string;
	headerComponentElements: {
		HeaderIcon?: PanelElement;
		headerLabel?: MessageDescriptor;
		HeaderAfterIconElement?: PanelElement;
		HeaderRightAlignedElement?: PanelElement;
		HeaderBeforeIconElement?: PanelElement;
		headerStyles?: {
			setGrayBackground?: boolean;
		};
	};
	BodyComponent: PanelElement;
	FooterComponent?: PanelElement;
	closeOptions?: ObjectSidebarPanelCloseOptions;
	props?: PanelProps;
};

export type ObjectSidebarState = {
	isPanelScrolled: boolean;
	isObjectSidebarShown: boolean;
	behavior: ObjectSidebarBehavior;
	panelWidth: number;
	panel?: ObjectSidebarPanel;
	updatedPanelHeaderComponentElements?: ObjectSidebarPanel['headerComponentElements'];
	sidebarControlState: ObjectSidebarControlState;
	sidebarControlExpandCollapsedState: ObjectSidebarControlExpandCollapsedState;
	activeActionInPanel?: AIAction;
};

export const initialState: ObjectSidebarState = {
	isPanelScrolled: false,
	isObjectSidebarShown: false,
	behavior: 'push',
	panelWidth: defaultPanelWidth,
	panel: undefined,
	updatedPanelHeaderComponentElements: undefined,
	sidebarControlState: window.__SSR_RENDERED__ ? 'shown' : 'hidden',
	sidebarControlExpandCollapsedState: window.__SSR_RENDERED__ ? 'expanded' : 'collapsed',
	activeActionInPanel: undefined,
};

export const actions = {
	setIsPanelScrolled:
		(isPanelScrolled: boolean) =>
		({ setState }: StoreActionApi<ObjectSidebarState>) => {
			setState({ isPanelScrolled });
		},
	setSidebarControlState:
		(sidebarControlState: ObjectSidebarControlState) =>
		({ setState }: StoreActionApi<ObjectSidebarState>) => {
			setState({ sidebarControlState });
		},
	setSidebarControlExpandCollapsedState:
		(sidebarControlExpandCollapsedState: ObjectSidebarControlExpandCollapsedState) =>
		({ setState }: StoreActionApi<ObjectSidebarState>) => {
			setState({ sidebarControlExpandCollapsedState });
		},
	toggleObjectSidebarControl:
		() =>
		({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const { sidebarControlExpandCollapsedState } = getState();
			if (sidebarControlExpandCollapsedState === 'collapsed') {
				setState({ sidebarControlExpandCollapsedState: 'expanded' });
			} else {
				setState({ sidebarControlExpandCollapsedState: 'collapsed' });
			}
		},
	showObjectSidebar:
		(panel: ObjectSidebarPanel, behavior?: ObjectSidebarBehavior, panelWidth?: number) =>
		({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const {
				isObjectSidebarShown,
				panel: currentPanel,
				behavior: currentBehavior,
				panelWidth: currentPanelWidth,
			} = getState();

			// Check if panel is open
			if (isObjectSidebarShown) {
				// If it's the same panel do nothing
				if (currentPanel?.id === panel.id) {
					return;
				}

				// Otherwise change it
				setState({
					panel,
					updatedPanelHeaderComponentElements: undefined,
					behavior: behavior ?? currentBehavior,
					panelWidth: panelWidth ?? currentPanelWidth,
					isPanelScrolled: false,
				});
			}

			// Set the panel to be open
			setState({
				isObjectSidebarShown: true,
				panel,
				updatedPanelHeaderComponentElements: undefined,
				behavior: behavior ?? currentBehavior,
				panelWidth: panelWidth ?? currentPanelWidth,
				sidebarControlState: 'hidden',
				isPanelScrolled: false,
			});

			// Close the global panel
			window.dispatchEvent(new Event('closeGlobalSidebar'));
		},
	hideObjectSidebar:
		(afterCloseCallback?: () => void) =>
		async ({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const { isObjectSidebarShown, panel } = getState();

			// If we're not showing a panel, don't do anything
			if (!isObjectSidebarShown || !panel) {
				return;
			}

			// If the user has provided closeOptions check them
			if (panel.closeOptions) {
				const { canClosePanel, onPanelClose } = panel.closeOptions;

				// If the panel closing check returns false, do nothing
				if (canClosePanel) {
					const panelCanClose = await canClosePanel();

					if (!panelCanClose) {
						return;
					}
				}

				// Call the onClose callback
				onPanelClose && onPanelClose();
			}

			// Close the panel
			setState({
				isObjectSidebarShown: false,
				panel: undefined,
				updatedPanelHeaderComponentElements: undefined,
				panelWidth: defaultPanelWidth,
				sidebarControlState: 'shown',
				isPanelScrolled: false,
			});

			if (afterCloseCallback) {
				afterCloseCallback();
			}
		},
	hideObjectSidebarByPanelId:
		(id: ObjectSidebarPanel['id']) =>
		({ getState, dispatch }: StoreActionApi<ObjectSidebarState>) => {
			const { panel } = getState();

			if (panel?.id === id) {
				void dispatch(actions.hideObjectSidebar());
			}
		},
	setPanelWidth:
		(newPanelWidth: number) =>
		({ setState }: StoreActionApi<ObjectSidebarState>) => {
			setState({ panelWidth: Math.max(newPanelWidth, MIN_PANEL_WIDTH) + CONTENT_RIGHT_PADDING });
		},
	changePanelBehavior:
		(newBehavior: ObjectSidebarBehavior) =>
		({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const { panelWidth } = getState();

			// When we set the new state, if it's set to cover, we no longer have a
			// component that will size the panel accordingly so we just use the default
			setState({
				behavior: newBehavior,
				panelWidth: newBehavior === 'cover' ? defaultPanelWidth : panelWidth,
			});
		},
	changePanel:
		(newPanel: ObjectSidebarPanel, newBehavior?: ObjectSidebarBehavior, newPanelWidth?: number) =>
		async ({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const { isObjectSidebarShown, panel, behavior, panelWidth } = getState();

			// Only change panels if the panel is open
			if (isObjectSidebarShown && panel) {
				// If it's the same panel do nothing
				if (panel.id === newPanel.id) {
					return;
				}

				// If the user has provided closeOptions check them
				if (panel.closeOptions) {
					const { canClosePanel, onPanelClose } = panel.closeOptions;

					// If the panel closing check returns false, do nothing
					if (canClosePanel) {
						const panelCanClose = await canClosePanel();
						if (!panelCanClose) {
							return;
						}
					}

					// Call the onClose callback
					onPanelClose && onPanelClose();
				}

				setState({
					panel: newPanel,
					updatedPanelHeaderComponentElements: undefined,
					behavior: newBehavior ?? behavior,
					panelWidth: newPanelWidth ?? panelWidth,
					isPanelScrolled: false,
				});
			}

			// Otherwise do nothing
		},
	changeHeaderElements:
		(
			id: ObjectSidebarPanel['id'],
			headerComponentElements: ObjectSidebarPanel['headerComponentElements'],
		) =>
		({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const { panel } = getState();
			if (panel?.id === id) {
				setState({
					updatedPanelHeaderComponentElements: headerComponentElements,
				});
			}
		},
	setActiveActionInPanel:
		(activeActionInPanel?: AIAction) =>
		({ setState, getState }: StoreActionApi<ObjectSidebarState>) => {
			const { activeActionInPanel: currentActiveAction } = getState();
			// If the active action is the same, do nothing
			if (currentActiveAction?.title === activeActionInPanel?.title) {
				return;
			}

			setState({
				activeActionInPanel,
			});
		},
};

export type ObjectSidebarActions = typeof actions;

export const ObjectSidebarStore = createStore({
	initialState,
	actions,
	name: 'ObjectSidebarStore',
});

type ObjectSidebarActionReturnType = {
	[K in keyof ObjectSidebarActions]: (
		...args: Parameters<ObjectSidebarActions[K]>
	) => ReturnType<ReturnType<ObjectSidebarActions[K]>>;
};

const useObjectSidebarActionsInternal = createActionsHook(ObjectSidebarStore);

export const useObjectSidebarActions: () => ObjectSidebarActionReturnType = () => {
	const internalActions = useObjectSidebarActionsInternal();

	const { close } = usePreviewPanelsActions();

	const showObjectSidebarWrapper = useCallback(
		(panel: ObjectSidebarPanel, behavior?: ObjectSidebarBehavior, panelWidth?: number) => {
			internalActions.showObjectSidebar(panel, behavior, panelWidth);
			void close();
		},
		[internalActions, close],
	);

	const showObjectSidebar = useMemo(
		() =>
			expValEquals('confluence_preview_panels_exp', 'isEnabled', true)
				? showObjectSidebarWrapper
				: internalActions.showObjectSidebar,
		[showObjectSidebarWrapper, internalActions.showObjectSidebar],
	);

	const memoizedActions = useMemo(
		() => ({
			...internalActions,
			showObjectSidebar,
		}),
		[internalActions, showObjectSidebar],
	);

	return useMemo(() => memoizedActions, [memoizedActions]);
};

export const useObjectSidebarState = createStateHook(ObjectSidebarStore);
const getIsObjectSidebarShown = (state: ObjectSidebarState) => state.isObjectSidebarShown;
const getPanel = (state: ObjectSidebarState) => state.panel;
const getBehavior = (state: ObjectSidebarState) => state.behavior;
const isPanelScrolled = (state: ObjectSidebarState) => state.isPanelScrolled;
const getPanelWidth = (state: ObjectSidebarState) => state.panelWidth;
const getUpdatedHeaderComponentElements = (state: ObjectSidebarState) =>
	state.updatedPanelHeaderComponentElements;
const getSidebarControlState = (state: ObjectSidebarState) => state.sidebarControlState;
const getSidebarControlExpandCollapsedState = (state: ObjectSidebarState) =>
	state.sidebarControlExpandCollapsedState;
const getActiveAction = (state: ObjectSidebarState) => state.activeActionInPanel;
// A selector just for the isObjectSidebarShown state since this is used in many places but we dont want to subscribe to the whole state
export const useIsObjectSidebarShown = createStateHook(ObjectSidebarStore, {
	selector: getIsObjectSidebarShown,
});

export const usePanel = createStateHook(ObjectSidebarStore, {
	selector: getPanel,
});
export const useBehavior = createStateHook(ObjectSidebarStore, {
	selector: getBehavior,
});
export const useIsPanelScrolled = createStateHook(ObjectSidebarStore, {
	selector: isPanelScrolled,
});
export const usePanelWidth = createStateHook(ObjectSidebarStore, {
	selector: getPanelWidth,
});
export const useUpdatedHeaderComponentElements = createStateHook(ObjectSidebarStore, {
	selector: getUpdatedHeaderComponentElements,
});
export const useSidebarControlState = createStateHook(ObjectSidebarStore, {
	selector: getSidebarControlState,
});
export const useSidebarControlExpandCollapsedState = createStateHook(ObjectSidebarStore, {
	selector: getSidebarControlExpandCollapsedState,
});
export const useActiveActionInPanel = createStateHook(ObjectSidebarStore, {
	selector: getActiveAction,
});
